const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const stripFrontmatter = (markdown) =>
  String(markdown || "").replace(
    /^\s*---\r?\n[\s\S]*?\r?\n---\r?\n?/,
    "",
  );

const safeHref = (value) => {
  const url = String(value || "").trim();
  if (/^(https?:\/\/|mailto:|\/(?!\/)|#)/i.test(url)) return url;
  return "#";
};

const inlineMarkdown = (escaped) =>
  escaped
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, "$1<em>$2</em>")
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_match, text, url) => {
      return `<a href="${safeHref(url)}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    });

const splitTableRow = (line) => {
  const raw = String(line || "").trim().replace(/^\|/, "").replace(/\|$/, "");
  return raw.split("|").map((cell) => cell.trim());
};

const isTableSep = (line) =>
  /^\s*\|?[\s:|-]+\|[\s:|-]*\|?\s*$/.test(line) && /---/.test(line);

export function renderSkillMarkdown(raw) {
  const lines = stripFrontmatter(raw).split(/\r?\n/);
  const output = [];
  let inCode = false;
  let codeBuffer = [];
  let listType = null;
  let listBuffer = [];
  let i = 0;

  const flushList = () => {
    if (!listType) return;
    output.push(`<${listType}>${listBuffer.join("")}</${listType}>`);
    listBuffer = [];
    listType = null;
  };

  while (i < lines.length) {
    const line = lines[i];

    if (/^```/.test(line)) {
      if (inCode) {
        output.push(
          `<pre class="md-code"><code>${escapeHtml(codeBuffer.join("\n"))}</code></pre>`,
        );
        codeBuffer = [];
        inCode = false;
      } else {
        flushList();
        inCode = true;
      }
      i += 1;
      continue;
    }
    if (inCode) {
      codeBuffer.push(line);
      i += 1;
      continue;
    }
    if (!line.trim()) {
      flushList();
      i += 1;
      continue;
    }

    // GFM table: header + separator + body rows
    if (
      line.includes("|") &&
      i + 1 < lines.length &&
      isTableSep(lines[i + 1])
    ) {
      flushList();
      const headers = splitTableRow(line);
      i += 2;
      const body = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) {
        if (isTableSep(lines[i])) {
          i += 1;
          continue;
        }
        body.push(splitTableRow(lines[i]));
        i += 1;
      }
      const thead = `<thead><tr>${headers
        .map((h) => `<th>${inlineMarkdown(escapeHtml(h))}</th>`)
        .join("")}</tr></thead>`;
      const tbody = `<tbody>${body
        .map(
          (row) =>
            `<tr>${headers
              .map((_, idx) => {
                const cell = row[idx] ?? "";
                return `<td>${inlineMarkdown(escapeHtml(cell))}</td>`;
              })
              .join("")}</tr>`,
        )
        .join("")}</tbody>`;
      output.push(`<div class="md-table-wrap"><table>${thead}${tbody}</table></div>`);
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      flushList();
      const level = heading[1].length;
      output.push(
        `<h${level}>${inlineMarkdown(escapeHtml(heading[2]))}</h${level}>`,
      );
      i += 1;
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      flushList();
      output.push("<hr/>");
      i += 1;
      continue;
    }

    const ordered = line.match(/^\s*\d+\.\s+(.*)$/);
    if (ordered) {
      if (listType !== "ol") {
        flushList();
        listType = "ol";
      }
      listBuffer.push(`<li>${inlineMarkdown(escapeHtml(ordered[1]))}</li>`);
      i += 1;
      continue;
    }
    const unordered = line.match(/^\s*[-*+]\s+(.*)$/);
    if (unordered) {
      if (listType !== "ul") {
        flushList();
        listType = "ul";
      }
      listBuffer.push(`<li>${inlineMarkdown(escapeHtml(unordered[1]))}</li>`);
      i += 1;
      continue;
    }
    // Merge consecutive blockquote lines into one block
    const quote = line.match(/^>\s?(.*)$/);
    if (quote) {
      flushList();
      const parts = [quote[1]];
      i += 1;
      while (i < lines.length) {
        const m = lines[i].match(/^>\s?(.*)$/);
        if (!m) break;
        parts.push(m[1]);
        i += 1;
      }
      output.push(
        `<blockquote>${parts
          .map((p) => inlineMarkdown(escapeHtml(p)))
          .join("<br/>")}</blockquote>`,
      );
      continue;
    }

    flushList();
    output.push(`<p>${inlineMarkdown(escapeHtml(line))}</p>`);
    i += 1;
  }

  if (inCode) {
    output.push(
      `<pre class="md-code"><code>${escapeHtml(codeBuffer.join("\n"))}</code></pre>`,
    );
  }
  flushList();
  return output.join("");
}
